"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/contexts/AuthContext"
import { BookingService } from "@/lib/services/bookingService"
import { SlotService } from "@/lib/services/slotService"
import type { BookingWithDetails } from "@/lib/supabase"
import { ArrowLeft, Calendar, Clock, Users, Ban, AlertTriangle, Shield } from "lucide-react"
import { toast } from "@/hooks/use-toast"
import QRCode from "qrcode";
import jsPDF from "jspdf" 

const BookingHistory = () => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<BookingWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [slotBlockReasons, setSlotBlockReasons] = useState<Record<string, string>>({})

  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (isLoading) return

    if (!isAuthenticated || user?.type !== "user") {
      navigate("/user-login")
      return
    }

    loadBookings()
  }, [isAuthenticated, user, navigate, isLoading])

  const loadBookings = async () => {
    if (!user || user.type !== "user") return

    setLoading(true)
    try {
      const userBookings = await BookingService.getUserBookings(user.id)
      setBookings(userBookings)

      // Load blocking reasons for rejected bookings
      const rejectedBookings = userBookings.filter(b => b.status === 'rejected')
      const blockReasons: Record<string, string> = {}
      
      for (const booking of rejectedBookings) {
        const reason = await SlotService.getSlotBlockingReason(booking.slot_id)
        if (reason) {
          blockReasons[booking.slot_id] = reason
        }
      }
      
      setSlotBlockReasons(blockReasons)
    } catch (error) {
      console.error("Error loading bookings:", error)
      toast({
        title: "Error",
        description: "Failed to load booking history",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800"
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "rejected":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getStatusLabel = (booking: BookingWithDetails) => {
    if (booking.status === "rejected") {
      const blockReason = slotBlockReasons[booking.slot_id]
      if (blockReason === "Admin Booking") {
        return "Cancelled (Admin Booking)"
      } else if (blockReason) {
        return "Cancelled (Blocked)"
      }
      return "Rejected"
    }
    return booking.status.charAt(0).toUpperCase() + booking.status.slice(1)
  }

  const canCancelBooking = (booking: BookingWithDetails) => {
    const bookingDate = new Date(booking.slot.date)
    const now = new Date()
    const hoursUntilBooking = (bookingDate.getTime() - now.getTime()) / (1000 * 60 * 60)
    return booking.status === "approved" && hoursUntilBooking > 24
  }

  const handleCancelBooking = async (bookingId: string) => {
    if (!user || user.type !== "user") return

    try {
      const success = await BookingService.cancelBooking(bookingId, user.id)

      if (success) {
        toast({
          title: "Booking Cancelled",
          description: "Your booking has been cancelled successfully.",
        })
        // Reload bookings to reflect the change
        loadBookings()
      } else {
        toast({
          title: "Cancellation Failed",
          description: "Unable to cancel booking. Please try again or contact support.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error cancelling booking:", error)
      toast({
        title: "Error",
        description: "An error occurred while cancelling the booking.",
        variant: "destructive",
      })
    }
  }

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":")
      const hour = Number.parseInt(hours)
      const ampm = hour >= 12 ? "PM" : "AM"
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      return `${displayHour}:${minutes} ${ampm}`
    }

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`
  }

const handleDownloadPass = (booking) => {
  const doc = new jsPDF("p", "mm", "a4");
  const margin = 10;
  let y = margin;
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoUrl = "/logo-placeholder.png"; // Place your logo in the public folder
  const logoWidth = 15;
  const logoHeight = 15;
  const headerBarHeight = 30;

  const img = new window.Image();
  img.src = logoUrl;
  img.crossOrigin = "anonymous";
  img.onload = () => {
    // ===== HEADER BAR =====
    doc.setFillColor(0, 102, 204); // Blue header
    doc.rect(0, 0, pageWidth, headerBarHeight, "F");

    // Center logo in header bar
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = (headerBarHeight - logoHeight) / 2 - 5; // Adjusted for vertical centering
    doc.addImage(img, "PNG", logoX, logoY, logoWidth, logoHeight);

    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(
      "Municipal Corporation Turf Booking Pass",
      pageWidth / 2,
      headerBarHeight - 4,
      { align: "center" }
    );

    y = headerBarHeight + 10;

    // ===== BORDER BOX =====
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 240, 5, 5);

    y += 10;

    // ===== PASS ID + STATUS =====
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`Pass ID: ${booking.id}`, margin + 5, y);
    doc.text(
      `Status: ${booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}`,
      pageWidth - margin - 5,
      y,
      { align: "right" }
    );

    y += 8;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin + 2, y, pageWidth - margin - 2, y);
    y += 8;

    // ===== BOOKING DETAILS =====
    doc.setFillColor(240, 240, 240);
    doc.rect(margin + 2, y - 6, pageWidth - margin * 2 - 4, 8, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Booking Details", margin + 5, y);

    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Booking Date: ${new Date(booking.created_at).toLocaleDateString()}`, margin + 5, y);
    doc.text(`Slot Date: ${booking.slot.date}`, pageWidth / 2, y);
    y += 7;
    doc.text(`Slot Time: ${booking.slot.time_start} - ${booking.slot.time_end}`, margin + 5, y);

    y += 12;

    // ===== USER INFO =====
    doc.setFillColor(240, 240, 240);
    doc.rect(margin + 2, y - 6, pageWidth - margin * 2 - 4, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.text("Booked By", margin + 5, y);

    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`${user.username || ""} (${user.phoneNo || ""})`, margin + 5, y);

    y += 12;

    // ===== TEAM MEMBERS =====
    doc.setFillColor(240, 240, 240);
    doc.rect(margin + 2, y - 6, pageWidth - margin * 2 - 4, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.text("Team Members", margin + 5, y);

    y += 10;
    doc.setFont("helvetica", "normal");
    if (booking.team_members?.length > 0) {
      booking.team_members.forEach((member, idx) => {
        doc.text(
          `${idx + 1}. ${member.name}${member.phone_number ? " (" + member.phone_number + ")" : ""}`,
          margin + 10,
          y
        );
        y += 6;
      });
    } else {
      doc.text("None", margin + 10, y);
      y += 6;
    }

    y += 8;

    // ===== REJECTION REASON =====
    if (booking.status === "rejected" && booking.rejection_reason) {
      doc.setFillColor(255, 230, 230);
      doc.rect(margin + 2, y - 6, pageWidth - margin * 2 - 4, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setTextColor(200, 0, 0);
      doc.text("Rejection Reason", margin + 5, y);

      y += 10;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(booking.rejection_reason, margin + 5, y);
      y += 10;
    }

    // ===== QR CODE =====
    const qrData = `Pass ID: ${booking.id} | Slot: ${booking.slot.date} ${booking.slot.time_start} | ${booking.slot.time_end} | User: ${user.username}`;
    QRCode.toDataURL(qrData, { width: 50, margin: 1 }, function (err, url) {
      if (!err) {
        doc.addImage(url, "PNG", pageWidth - margin - 55, y, 50, 50);

        // ===== FOOTER =====
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text(
          "Please carry this pass to the turf. For queries, contact the Municipal Corporation.",
          pageWidth / 2,
          290,
          { align: "center" }
        );

        doc.save(`TurfPass_${booking.id}.pdf`);
      } else {
        // Fallback if QR code generation fails
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text(
          "Please carry this pass to the turf. For queries, contact the Municipal Corporation.",
          pageWidth / 2,
          290,
          { align: "center" }
        );

        doc.save(`TurfPass_${booking.id}.pdf`);
      }
    });
  };
};
  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) return null

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading booking history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button variant="ghost" onClick={() => navigate("/user-dashboard")} className="mr-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Booking History</h1>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {bookings.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven't made any turf bookings yet. Start by booking your first slot!
              </p>
              <Button onClick={() => navigate("/booking")} className="bg-blue-600 hover:bg-blue-700">
                Book Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {bookings.map((booking) => {
              const blockReason = slotBlockReasons[booking.slot_id]
              const isBlocked = booking.status === "rejected" && blockReason
              
              return (
                <Card key={booking.id} className={isBlocked ? "border-l-4 border-l-orange-500" : ""}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <span>{booking.slot.date}</span>
                          <Badge className={getStatusColor(booking.status)}>
                            {getStatusLabel(booking)}
                          </Badge>
                          {isBlocked && (
                            <Ban className="w-4 h-4 text-orange-600" />
                          )}
                        </CardTitle>
                        <CardDescription>Booking ID: {booking.id}</CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{new Date(booking.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    {isBlocked && (
                      <Alert className="mb-4 border-orange-200 bg-orange-50">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-800">
                          <strong>Booking Cancelled by Admin:</strong> {blockReason}
                          {blockReason === "Admin Booking" && (
                            <div className="mt-1 text-sm">
                              This slot was needed for official use. You can book other available slots.
                            </div>
                          )}
                          {booking.rejection_reason && (
                            <div className="mt-2 text-sm text-red-700">
                              <strong>Reason:</strong> {booking.rejection_reason}
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <div className="font-medium">Time Slot</div>
                          <div className="text-sm text-gray-600">
                            {formatTimeSlot(booking.slot.time_start, booking.slot.time_end)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <div>
                          <div className="font-medium">Team Size</div>
                          <div className="text-sm text-gray-600">{booking.team_members.length} members</div>
                        </div>
                      </div>
                    </div>

                    {booking.team_members.length > 0 && (
                      <div className="mb-4">
                        <div className="font-medium mb-2">Team Members:</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {booking.team_members.map((teammate, index) => (
                            <div key={index} className="text-sm bg-gray-50 p-2 rounded">
                              <div className="font-medium">{teammate.name}</div>
                              {teammate.phone_number && <div className="text-gray-600">{teammate.phone_number}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        {booking.status === "pending" && (
                          <Badge variant="outline" className="text-yellow-600">
                            Awaiting Admin Approval
                          </Badge>
                        )}
                        {booking.status === "approved" && (
                          <Badge variant="outline" className="text-green-600">
                            Confirmed
                          </Badge>
                        )}
                        {booking.status === "rejected" && !blockReason && (
                          <Badge variant="outline" className="text-red-600">
                            Rejected
                          </Badge>
                        )}
                        {booking.status === "rejected" && booking.rejection_reason && !blockReason && (
                          <span className="ml-2 text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                            <strong>Reason:</strong> {booking.rejection_reason}
                          </span>
                        )}
                        {isBlocked && (
                          <Badge variant="outline" className="text-orange-600">
                            {blockReason === "Admin Booking" ? (
                              <>
                                <Shield className="w-3 h-3 mr-1" />
                                Admin Booking
                              </>
                            ) : (
                              <>
                                <Ban className="w-3 h-3 mr-1" />
                                Blocked
                              </>
                            )}
                          </Badge>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        {booking.status === "approved" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPass(booking)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            Download Pass
                          </Button>
                        )}
                        {canCancelBooking(booking) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelBooking(booking.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Cancel Booking
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

export default BookingHistory